import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import LoginLayout from '../LoginLayout/LoginLayout';
import AppLayout from '../AppLayout/AppLayout';
import GlobalResultsPage from '../../pages/GlobalResultsPage/GlobalResultsPage';
import CycleResultsPage from '../../pages/CycleResultsPage/CycleResultsPage';
import ChartsPage from '../../pages/ChartsPage/ChartsPage';
import AttendancePage from '../../pages/AttendancePage/AttendancePage';
import CodeReviewPage from '../../pages/CodeReviewPage/CodeReviewPage';
import MoodleStatsPage from '../../pages/MoodleStatsPage/MoodleStatsPage';
import AdminGitlabPage from '../../pages/AdminGitlabPage/AdminGitlabPage';
import AdminUsersPage from '../../pages/AdminUsersPage/AdminUsersPage';
import AdminGroupsPage from '../../pages/AdminGroupsPage/AdminGroupsPage';
import AdminTokensPage from '../../pages/AdminTokensPage/AdminTokensPage';
import AdminMoodleSyncPage from '../../pages/AdminMoodleSyncPage/AdminMoodleSyncPage';
import AdminCyclesPage from '../../pages/AdminCyclesPage/AdminCyclesPage';
import StudentRegistrationPage from '../../pages/StudentRegistrationPage/StudentRegistrationPage';
import ProjectGroupSelectionPage from '../../pages/ProjectGroupSelectionPage/ProjectGroupSelectionPage';
import LoginForm, { REDIRECT_AFTER_LOGIN_KEY } from '../LoginForm/LoginForm';
import { useAuth } from '../../providers/AuthProvider';

// Après la connexion CAS (/login/callback) : retour à la page mémorisée avant la redirection
// vers le CAS (ex. session expirée sur une page de cycle), sinon accueil.
const RedirectAfterLogin: React.FC = () => {
  const navigate = useNavigate();
  useEffect(() => {
    let target = '/';
    try {
      target = sessionStorage.getItem(REDIRECT_AFTER_LOGIN_KEY) || '/';
      sessionStorage.removeItem(REDIRECT_AFTER_LOGIN_KEY);
    } catch {
      // sessionStorage indisponible : accueil
    }
    navigate(target, { replace: true });
  }, [navigate]);
  return null;
};

// Routes de l'application authentifiée, extraites telles quelles (comportement inchangé) pour
// pouvoir cohabiter avec une route publique (auto-inscription étudiante) au niveau supérieur.
const AuthenticatedRoutes: React.FC = () => (
  <Routes>
    <Route path="/" element={<AppLayout />}>
      <Route index element={<GlobalResultsPage />} />
      <Route path="cycle/:cycleId" element={<CycleResultsPage />} />
      <Route path="charts" element={<ChartsPage />} />
      <Route path="assiduite" element={<AttendancePage />} />
      <Route path="revue-code" element={<CodeReviewPage />} />
      <Route path="suivi-moodle" element={<MoodleStatsPage />} />
      <Route path="admin/gitlab" element={<AdminGitlabPage />} />
      <Route path="admin/users" element={<AdminUsersPage />} />
      <Route path="admin/groups" element={<AdminGroupsPage />} />
      <Route path="admin/tokens" element={<AdminTokensPage />} />
      <Route path="admin/moodle-sync" element={<AdminMoodleSyncPage />} />
      <Route path="admin/cycles" element={<AdminCyclesPage />} />
    </Route>
    {/* Chemin transitoire (ex: /login/callback) où isAuthenticated vient de passer à true
        pendant que l'URL n'a pas encore été changée — sans ça, React Router n'affiche rien. */}
    <Route path="login/*" element={<RedirectAfterLogin />} />
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

const AppRouter: React.FC = () => {
  const { authState } = useAuth();

  return (
    <Routes>
      {/* Publique : étudiant·es pas encore capables de se logger normalement (voir auto-inscription) */}
      <Route path="/inscription" element={<StudentRegistrationPage />} />
      <Route path="/inscription/callback" element={<StudentRegistrationPage />} />
      <Route path="/groupe-projet" element={<ProjectGroupSelectionPage />} />
      <Route path="/groupe-projet/callback" element={<ProjectGroupSelectionPage />} />
      <Route
        path="/*"
        element={
          authState.isAuthenticated ? (
            <AuthenticatedRoutes />
          ) : (
            <LoginLayout>
              <LoginForm />
            </LoginLayout>
          )
        }
      />
    </Routes>
  );
};

export default AppRouter;
